import { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useIsNewEntry, usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { store } from "src/redux/store";
import { isOnline } from "src/utils";
import {
  setEditing,
  setEntryLockData,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";

const KEEP_ALIVE_DELAY_MS = 5000;
const KEEP_ALIVE_INTERVAL_MS = 5000;
const CHECK_LOCK_INTERVAL_MS = 3000;

export interface EntryLockOptions {
  /** V1: skip all lock logic while the entry is locked for 24h. */
  respectLocked24?: boolean;
  /** V1: force editing off when the entry is locked by another user. */
  stopEditingWhenLockedByOther?: boolean;
  /** V2: treat an open save modal as "editing/saving" (keep the lock). */
  keepLockDuringSaveModal?: boolean;
  /** V2: release the lock when the save modal is canceled. */
  releaseOnSaveModalCancel?: boolean;
  /** V1: pause keep-alive while the refetch modal is open. */
  pauseKeepAliveDuringRefetchModal?: boolean;
  /** V1: self-release from the keep-alive interval if the URL left the logbook. */
  releaseWhenLeavingLogbookDuringKeepAlive?: boolean;
}

/**
 * Shared entry-lock controller used by both the V1 and V2 generators. The small
 * behavioral differences between them are expressed through `options`.
 */
export const useEntryLockController = (options: EntryLockOptions = {}) => {
  const { post } = usePostApi();
  const { idType, lbeidOrPartId } = useParams();

  const locked24 = useSelector(state => state.generatorSlice.locked24);
  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const current = useSelector(state => state.entrySlice.current);
  const editing = useSelector(state => state.entrySlice.editing);
  const activeAutosave = useSelector(state => state.entrySlice.activeAutosave);
  const locked = useSelector(state => state.entrySlice.locked);
  const you = useSelector(state => state.entrySlice.you);
  const openedSaveModal = useSelector(
    state => state.entrySlice.openedSaveModal,
  );
  const releaseEntryLock = useSelector(
    state => state.entrySlice.releaseEntryLock,
  );

  const isNewEntry = useIsNewEntry();
  const dispatch = useDispatch();

  const idObject = useMemo(() => {
    const entryId = isNewEntry ? "new" : id || lbeidOrPartId || "";
    return { [String(idType)]: entryId };
  }, [id, idType, isNewEntry, lbeidOrPartId]);

  const lastExistingIdObjectRef = useRef<Record<string, string> | null>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const keepAliveDelayRef = useRef<NodeJS.Timeout | null>(null);
  const releaseInFlightRef = useRef(false);
  const pendingAcquireRef = useRef(false);
  // Acquire requested while a release was in flight; run it once that settles.
  const acquireAfterReleaseRef = useRef(false);
  const didStaleReleaseRef = useRef(false);
  const prevEditingRef = useRef(editing);
  const prevOpenedSaveModalRef = useRef(openedSaveModal);

  // `openedSaveModal` only affects lock lifecycle for variants that opt in.
  const savingActive = options.keepLockDuringSaveModal ? openedSaveModal : false;
  const isEditingOrSaving = editing || savingActive;
  const locked24Applies = Boolean(options.respectLocked24) && locked24;

  useEffect(() => {
    if (
      !isNewEntry &&
      idObject[String(idType)] &&
      idObject[String(idType)] !== "new"
    ) {
      lastExistingIdObjectRef.current = { ...idObject };
    }
  }, [idObject, idType, isNewEntry]);

  const clearKeepAlive = useCallback(() => {
    if (keepAliveDelayRef.current) {
      clearTimeout(keepAliveDelayRef.current);
      keepAliveDelayRef.current = null;
    }
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, []);

  const getLock = useCallback(() => {
    if (!isOnline()) return;
    if (releaseInFlightRef.current) {
      acquireAfterReleaseRef.current = true;
      return;
    }

    if (isNewEntry) {
      dispatch(
        setEntryLockData({
          locked: true,
          user: null,
          you: true,
          changed: 0,
          activeAutosave: true,
        }),
      );
      return;
    }

    const entryId = Object.values(idObject)[0];
    if (!entryId || entryId === "new") return;
    if (!current) {
      pendingAcquireRef.current = true;
      return;
    }

    pendingAcquireRef.current = false;
    void post({
      request: "get-entry-lock",
      body: { params: { ...idObject, current } },
      onSuccess: data => {
        dispatch(
          setEntryLockData({
            ...data,
            activeAutosave: data.locked && data.you,
            lockedByOther: data.locked && !data.you,
          }),
        );
      },
    });
  }, [current, dispatch, idObject, isNewEntry, post]);

  const checkLock = useCallback(() => {
    if (!isOnline()) return;
    if (isNewEntry) return;

    void post({
      request: "check-entry-lock",
      body: { params: { ...idObject, current } },
      onSuccess: data =>
        dispatch(
          setEntryLockData({
            ...data,
            activeAutosave: false,
          }),
        ),
    });
  }, [current, dispatch, idObject, isNewEntry, post]);

  const keepAlive = useCallback(() => {
    if (!isOnline()) return;
    if (isNewEntry) return;
    void post({
      request: "keep-alive-entry-lock",
      body: { params: { ...idObject } },
      onError: () => {
        if (releaseInFlightRef.current || !store.getState().entrySlice.editing) {
          return;
        }
        getLock();
      },
    });
  }, [getLock, idObject, isNewEntry, post]);

  const releaseLock = useCallback(async () => {
    // A concurrent release adds nothing; skip the duplicate request.
    if (releaseInFlightRef.current) return;
    releaseInFlightRef.current = true;
    clearKeepAlive();

    const clearLocalLock = () => {
      dispatch(
        setEntryLockData({
          locked: false,
          you: false,
          user: null,
          activeAutosave: false,
        }),
      );
    };

    try {
      if (!isOnline()) {
        clearLocalLock();
        return;
      }

      const releaseParams = !isNewEntry
        ? idObject
        : lastExistingIdObjectRef.current;

      if (releaseParams) {
        await post({
          request: "release-entry-lock",
          body: { params: { ...releaseParams } },
        });
      }

      clearLocalLock();
    } finally {
      releaseInFlightRef.current = false;
      if (acquireAfterReleaseRef.current) {
        acquireAfterReleaseRef.current = false;
        if (store.getState().entrySlice.editing) getLock();
      }
    }
  }, [clearKeepAlive, dispatch, getLock, idObject, isNewEntry, post]);

  // Force editing off when the entry is locked by another user (V1).
  useEffect(() => {
    if (locked24Applies) return;
    if (options.stopEditingWhenLockedByOther && locked && !you) {
      dispatch(setEditing(false));
    }
  }, [dispatch, locked, locked24Applies, options.stopEditingWhenLockedByOther, you]);

  // Viewing: poll the lock status while not editing.
  useEffect(() => {
    if (locked24Applies) return;

    const currentEntryId = Object.values(idObject)[0];
    const canCheckLock =
      Boolean(current) && Boolean(currentEntryId) && currentEntryId !== "new";

    const shouldPoll =
      !isEditingOrSaving &&
      canCheckLock &&
      (!locked || (locked && !you) || (locked && you && !activeAutosave));
    if (!shouldPoll) return;

    checkLock();
    const interval = setInterval(checkLock, CHECK_LOCK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [
    activeAutosave,
    checkLock,
    current,
    idObject,
    isEditingOrSaving,
    locked,
    locked24Applies,
    you,
  ]);

  // Acquire the lock only on the editing false->true transition.
  useEffect(() => {
    const prevEditing = prevEditingRef.current;
    prevEditingRef.current = editing;

    if (locked24Applies) return;

    const editingStarted = editing && !prevEditing;
    if (!editingStarted) return;

    const s = store.getState().entrySlice;
    if (s.activeAutosave) return;
    if (s.locked && !s.you) return;

    getLock();
  }, [editing, getLock, locked24Applies]);

  // Retry get-entry-lock once entry `current` is available (data still loading).
  useEffect(() => {
    if (!pendingAcquireRef.current) return;
    if (!current || !editing || activeAutosave) return;

    const s = store.getState().entrySlice;
    if (s.activeAutosave) return;
    if (s.locked && !s.you) return;

    getLock();
  }, [activeAutosave, current, editing, getLock, locked, you]);

  // A full page reload abandons the prior client session. If get-logbook still
  // reports our own lock, release it so the entry is not stuck until TTL.
  useEffect(() => {
    if (didStaleReleaseRef.current) return;
    if (locked24Applies || isNewEntry) return;
    if (!current) return;

    const entryId = Object.values(idObject)[0];
    if (!entryId || entryId === "new") return;

    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    const isReload = nav?.type === "reload";

    if (isReload && locked && you && !activeAutosave && !editing) {
      didStaleReleaseRef.current = true;
      void releaseLock();
    }
  }, [
    activeAutosave,
    current,
    editing,
    idObject,
    isNewEntry,
    locked,
    locked24Applies,
    releaseLock,
    you,
  ]);

  // Keep the lock alive while editing and holding it. Not applicable to new
  // entries: `activeAutosave`/`locked`/`you` are set locally for them (see
  // getLock), with no real server-held lock to keep alive.
  useEffect(() => {
    if (locked24Applies) return;

    if (isEditingOrSaving && activeAutosave && locked && you && !isNewEntry) {
      if (!keepAliveDelayRef.current && !keepAliveIntervalRef.current) {
        keepAliveDelayRef.current = setTimeout(() => {
          keepAliveDelayRef.current = null;

          const runKeepAlive = () => {
            if (
              options.pauseKeepAliveDuringRefetchModal &&
              store.getState().entrySlice.openedRefetchModal
            ) {
              return;
            }
            if (
              options.releaseWhenLeavingLogbookDuringKeepAlive &&
              !window.location.href.includes("logbook")
            ) {
              clearKeepAlive();
              void releaseLock();
              return;
            }
            keepAlive();
          };

          runKeepAlive();
          keepAliveIntervalRef.current = setInterval(
            runKeepAlive,
            KEEP_ALIVE_INTERVAL_MS,
          );
        }, KEEP_ALIVE_DELAY_MS);
      }
    } else {
      clearKeepAlive();
    }
  }, [
    activeAutosave,
    clearKeepAlive,
    isEditingOrSaving,
    isNewEntry,
    keepAlive,
    locked,
    locked24Applies,
    options.pauseKeepAliveDuringRefetchModal,
    options.releaseWhenLeavingLogbookDuringKeepAlive,
    releaseLock,
    you,
  ]);

  // Release the lock when the save modal is canceled (V2).
  useEffect(() => {
    if (!options.releaseOnSaveModalCancel) {
      prevOpenedSaveModalRef.current = openedSaveModal;
      return;
    }

    const wasCanceled = prevOpenedSaveModalRef.current && !openedSaveModal;
    if (wasCanceled && !editing && locked && you && releaseEntryLock === 0) {
      clearKeepAlive();
      void releaseLock();
    }

    prevOpenedSaveModalRef.current = openedSaveModal;
  }, [
    clearKeepAlive,
    editing,
    locked,
    openedSaveModal,
    options.releaseOnSaveModalCancel,
    releaseEntryLock,
    releaseLock,
    you,
  ]);

  // Release signal (e.g. dispatched by autosave on success).
  useEffect(() => {
    if (releaseEntryLock === 0) return;
    clearKeepAlive();
    void releaseLock();
    dispatch(setReleaseEntryLock(0));
  }, [clearKeepAlive, dispatch, releaseEntryLock, releaseLock]);

  // Release the lock on unmount when leaving the logbook.
  useEffect(() => {
    return () => {
      clearKeepAlive();
      if (!window.location.href.includes("logbook")) {
        void releaseLock();
      }
    };
  }, [clearKeepAlive, releaseLock]);
};
